import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MonitorSmartphone, ShieldCheck, Boxes } from "lucide-react";
import { api } from "../../lib/api";

const KIND: Record<string, string> = { OBICNA: "Obična kasa", HORECA: "HoReCa kasa" };

function statusOf(activeUntil: string | null): { t: string; cls: string } {
  if (!activeUntil) return { t: "Aktivan", cls: "bg-emerald-50 text-emerald-700" };
  const days = (new Date(activeUntil).getTime() - Date.now()) / 86400000;
  if (days < 0) return { t: "Istekao", cls: "bg-red-50 text-red-700" };
  if (days < 30) return { t: "Ističe uskoro", cls: "bg-amber-50 text-amber-700" };
  return { t: "Aktivan", cls: "bg-emerald-50 text-emerald-700" };
}

/** Moj paket: paketi Kasa + L-PFR (mesta), kase i L-PFR firme, sa prečicama do detalja. */
export function KasaLpfrCard() {
  const qc = useQueryClient();
  const dev = useQuery({ queryKey: ["my-devices"], queryFn: () => api.listMyDevices(), retry: false });
  const lp = useQuery({ queryKey: ["lpfr-overview"], queryFn: () => api.getLpfrOverview(), retry: false });
  const pk = useQuery({ queryKey: ["kasa-paketi"], queryFn: () => api.getKasaPaketi(), retry: false });
  const locs = useQuery({ queryKey: ["business-locations-opts"], queryFn: () => api.listBusinessLocationOptions(), retry: false });
  const [err, setErr] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: (v: { id: string; label?: string; businessLocationId?: string | null }) =>
      api.updateKasaPaket(v.id, { label: v.label, businessLocationId: v.businessLocationId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kasa-paketi"] }),
    onError: () => setErr("Čuvanje nije uspelo."),
  });
  const release = useMutation({
    mutationFn: (id: string) => api.releaseKasaPaket(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kasa-paketi"] }),
    onError: () => setErr("Oslobađanje nije uspelo."),
  });

  const devices = dev.data ?? [];
  const active = devices.filter((d) => !d.blocked);
  const lpfr = lp.data;
  const packages = pk.data;
  if (!devices.length && !lpfr?.module_active && !packages?.total) return null;

  const total = packages?.total ?? 0;
  return (
    <div className="space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-surface p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Boxes className="h-4 w-4 text-brand" aria-hidden /> Paketi Kasa + L-PFR
          </div>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {packages?.bound ?? 0}
            {total ? <span className="text-base font-medium text-slate-400"> / {total}</span> : null}
          </p>
          <p className="text-xs text-slate-500">
            {total ? "mesta je povezano sa karticom" : "Još nemate paket. Obratite se podršci ili naručite paket."}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-surface p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <MonitorSmartphone className="h-4 w-4 text-brand" aria-hidden /> Vaše kase
          </div>
          <p className="mt-1 text-2xl font-bold text-slate-900">{active.length}</p>
          <p className="text-xs text-slate-500">
            {active.length === 1 ? "kasa je prijavljena" : "kasa je prijavljeno"}
          </p>
          <Link to="/podesavanja?tab=kase" className="mt-1 inline-block text-sm font-medium text-brand hover:underline">
            Prikaži sve kase →
          </Link>
        </div>
        {lpfr?.module_active && (
          <div className="rounded-xl border border-border bg-surface p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ShieldCheck className="h-4 w-4 text-brand" aria-hidden /> Vaš L-PFR
            </div>
            <p className="mt-1 text-sm text-slate-700">
              Licenca: <span className="font-mono">{lpfr.license ? `${lpfr.license.key_prefix}…` : "nije kreirana"}</span>
            </p>
            <p className="text-xs text-slate-500">
              {lpfr.installations.length ? `${lpfr.installations.length} aktivnih instalacija` : "Još nema instalacija."}
            </p>
            <Link to="/podesavanja?tab=fiskalizacija" className="mt-1 inline-block text-sm font-medium text-brand hover:underline">
              Prikaži licencu i naloge →
            </Link>
          </div>
        )}
      </div>

      {total > 0 && (
        <div className="rounded-xl border border-border bg-surface">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-800">Paketi Kasa + L-PFR</h2>
            <p className="text-xs text-slate-400">
              Svaki paket je jedna kasa i jedan L-PFR, vezani za karticu (JID) i lokaciju. Za dodatnu kasu naručuje se novi paket.
            </p>
          </div>
          {err && <p className="px-4 py-2 text-sm text-red-600">{err}</p>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="px-4 py-2">Naziv</th>
                  <th className="px-4 py-2">Vrsta kase</th>
                  <th className="px-4 py-2">Kartica (JID)</th>
                  <th className="px-4 py-2">Lokacija</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Aktivan do</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {packages!.items.map((p) => {
                  const st = statusOf(p.activeUntil);
                  return (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-4 py-2">
                        <input
                          defaultValue={p.label ?? ""}
                          placeholder="npr. Kasa 1"
                          onBlur={(e) => e.target.value !== (p.label ?? "") && update.mutate({ id: p.id, label: e.target.value })}
                          className="h-8 w-36 rounded-lg border border-border px-2 text-sm"
                        />
                      </td>
                      <td className="px-4 py-2">{KIND[p.kind] ?? p.kind}</td>
                      <td className="px-4 py-2 font-mono text-xs">{p.jid ?? <span className="text-slate-400">čeka karticu</span>}</td>
                      <td className="px-4 py-2">
                        <select
                          value={p.businessLocationId ?? ""}
                          onChange={(e) => update.mutate({ id: p.id, businessLocationId: e.target.value || null })}
                          className="h-8 rounded-lg border border-border px-2 text-sm"
                        >
                          <option value="">— izaberi —</option>
                          {(locs.data ?? []).map((l) => (
                            <option key={l.id} value={l.id}>
                              {l.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.t}</span>
                      </td>
                      <td className="px-4 py-2 text-slate-600">
                        {p.activeUntil ? new Date(p.activeUntil).toLocaleDateString("sr-RS") : "—"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {p.jid && (
                          <button
                            className="text-xs text-slate-500 hover:text-red-600"
                            onClick={() =>
                              window.confirm("Osloboditi ovo mesto? Kartica (JID) se odvezuje i možete prijaviti novu.") &&
                              release.mutate(p.id)
                            }
                          >
                            Oslobodi mesto
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
